'use client';

import { useState } from 'react';
import { ChevronDownIcon, WrenchIcon } from 'lucide-react';
import Button from '@ui/Button';
import SummarizeChat from '../SummarizeChat';
import {
  useSupportChatActions,
  SupportActionContext,
  SupportAction,
} from '@util/hooks/useSupportChatActions';

interface SupportActionsRendererProps extends SupportActionContext {
  layout: 'dropdown' | 'horizontal';
}

export default function SupportActionsRenderer({
  layout,
  ...actionContext
}: SupportActionsRendererProps) {
  const { isSupport, getActionsByCategory } = useSupportChatActions();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (!isSupport) return null;

  const {
    admin,
    chat,
    case: caseActions,
  } = getActionsByCategory(actionContext);

  const renderAction = (action: SupportAction, compact = false) => {
    if (action.href) {
      return (
        <a
          key={action.id}
          href={action.href}
          target={action.target}
          rel={action.target === '_blank' ? 'noopener noreferrer' : undefined}
          className={
            compact
              ? 'block w-full px-6 py-3 text-left text-[1.6rem] text-gray-700 transition-colors hover:bg-gray-100'
              : ''
          }
          onClick={() => {
            action.onClick();
            if (layout === 'dropdown') setDropdownOpen(false);
          }}
        >
          {compact ? (
            action.label
          ) : (
            <Button
              height="small"
              type={action.type as any}
              width="small"
              text={action.label}
            />
          )}
        </a>
      );
    }

    if (compact) {
      return (
        <button
          key={action.id}
          onClick={() => {
            action.onClick();
            if (layout === 'dropdown') setDropdownOpen(false);
          }}
          disabled={action.loading}
          className="block w-full px-6 py-3 text-left text-[1.6rem] text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
        >
          {action.loading ? `${action.label}...` : action.label}
        </button>
      );
    }

    return (
      <Button
        key={action.id}
        height="small"
        type={action.type as any}
        width="small"
        text={action.label}
        loading={action.loading}
        onClick={action.onClick}
      />
    );
  };

  if (layout === 'horizontal') {
    const allActions = [...admin, ...chat, ...caseActions];

    return (
      <div className="flex flex-wrap items-center gap-[1.6rem] overflow-x-scroll">
        {allActions.map((action) => renderAction(action, false))}
        <SummarizeChat chat={actionContext.chat} />
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-3 rounded-[2rem] border border-gray-300 bg-white px-6 py-3 text-[1.6rem] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-secondary focus:ring-offset-2"
      >
        <div className="flex items-center gap-x-4">
          <WrenchIcon className="h-8 w-8" /> Support Actions
        </div>
        <ChevronDownIcon className="h-10 w-10" />
      </button>

      {dropdownOpen && (
        <div className="absolute left-0 z-50 mt-2 w-[24rem] origin-top-left rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-2">
            {admin.length > 0 && (
              <>
                <div className="px-6 py-3 text-[1.6rem] font-semibold uppercase tracking-wide text-gray-500">
                  Admin Actions
                </div>
                {admin.map((action) => renderAction(action, true))}
                <div className="my-2 border-t border-gray-200"></div>
              </>
            )}

            {chat.length > 0 && (
              <>
                <div className="px-6 py-3 text-[1.6rem] font-semibold uppercase tracking-wide text-gray-500">
                  Chat Actions
                </div>
                {chat.map((action) => renderAction(action, true))}
                <div className="my-2 border-t border-gray-200"></div>
              </>
            )}

            {caseActions.length > 0 && (
              <>
                <div className="px-6 py-3 text-[1.6rem] font-semibold uppercase tracking-wide text-gray-500">
                  Case Management
                </div>
                {caseActions.map((action) => renderAction(action, true))}
              </>
            )}

            <div className="px-6 py-2">
              <SummarizeChat chat={actionContext.chat} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
